import { computed, ref } from 'vue';
import type { Dayjs } from 'dayjs';
import { getLocalizedDayJs } from '../utils/time';
import { getWeekStartDayNumber } from '../utils/settings';

// Jours masqués dans le calendrier : dimanche (0) et samedi (6).
export const HIDDEN_WEEKDAYS = [0, 6];

export function isHiddenDay(date: Dayjs): boolean {
    return HIDDEN_WEEKDAYS.includes(date.day());
}

/** Avance jusqu'au premier jour affiché (lundi si on tombe un week-end). */
export function nextVisibleDay(date: Dayjs): Dayjs {
    let day = date;
    while (isHiddenDay(day)) {
        day = day.add(1, 'day');
    }
    return day;
}

/** Recule jusqu'au dernier jour affiché (vendredi si on tombe un week-end). */
export function previousVisibleDay(date: Dayjs): Dayjs {
    let day = date;
    while (isHiddenDay(day)) {
        day = day.subtract(1, 'day');
    }
    return day;
}

export function useCalendarNavigation(callbacks: {
    onDatesChange: (payload: { start: Dayjs; end: Dayjs }) => void;
    scrollToCurrentTime: () => void;
    initialDate?: Dayjs | null;
}) {
    const activeView = ref('timeGridWeek');
    const currentDate = ref(callbacks.initialDate ?? getLocalizedDayJs());

    function getFirstDay(): number {
        return getWeekStartDayNumber();
    }

    const viewDays = computed<Dayjs[]>(() => {
        const numDays = activeView.value === 'timeGridWeek' ? 7 : 1;

        if (numDays === 1) {
            // Vue "jour" : jamais de colonne vide, on décale sur le jour ouvré suivant
            // si la date courante tombe un week-end (lien profond ?date=..., ou
            // bouton "today" cliqué un samedi).
            return [nextVisibleDay(currentDate.value).startOf('day')];
        }

        const firstDay = getFirstDay();
        const today = currentDate.value.startOf('day');
        const offset = (today.day() - firstDay + 7) % 7;
        const weekStart = today.subtract(offset, 'day');

        const days: Dayjs[] = [];
        for (let i = 0; i < numDays; i++) {
            const day = weekStart.add(i, 'day');
            // Le week-end n'est pas affiché : la semaine rend 5 colonnes.
            // Le calcul reste basé sur la semaine complète, donc la préférence
            // "premier jour de la semaine" (dimanche ou lundi) est respectée.
            if (!isHiddenDay(day)) {
                days.push(day);
            }
        }
        return days;
    });

    const viewTitle = computed(() => {
        if (activeView.value === 'timeGridDay') {
            return currentDate.value.format('MMMM YYYY');
        }

        const days = viewDays.value;
        if (days.length === 0) return '';

        const first = days[0]!;
        const last = days[days.length - 1]!;

        if (first.year() !== last.year()) {
            return `${first.format('MMM YYYY')} \u2013 ${last.format('MMM YYYY')}`;
        }
        if (first.month() !== last.month()) {
            return `${first.format('MMM')} \u2013 ${last.format('MMM YYYY')}`;
        }
        return first.format('MMMM YYYY');
    });

    function emitDatesChange() {
        const days = viewDays.value;
        if (days.length === 0) return;

        const start = days[0]!;
        const end = days[days.length - 1]!.add(1, 'day');
        callbacks.onDatesChange({ start, end });
    }

    function handlePrev() {
        if (activeView.value === 'timeGridWeek') {
            currentDate.value = currentDate.value.subtract(7, 'day');
        } else {
            // Vue "jour" : le week-end est sauté (lundi -> vendredi précédent).
            currentDate.value = previousVisibleDay(currentDate.value.subtract(1, 'day'));
        }
        emitDatesChange();
        callbacks.scrollToCurrentTime();
    }

    function handleNext() {
        if (activeView.value === 'timeGridWeek') {
            currentDate.value = currentDate.value.add(7, 'day');
        } else {
            // Vue "jour" : le week-end est sauté (vendredi -> lundi suivant).
            currentDate.value = nextVisibleDay(currentDate.value.add(1, 'day'));
        }
        emitDatesChange();
        callbacks.scrollToCurrentTime();
    }

    function handleToday() {
        const today = getLocalizedDayJs();
        // Un samedi ou un dimanche, "today" ouvre le lundi suivant en vue jour
        // (sinon la vue serait vide) et la semaine en cours en vue semaine.
        currentDate.value = activeView.value === 'timeGridWeek' ? today : nextVisibleDay(today);
        emitDatesChange();
        callbacks.scrollToCurrentTime();
    }

    function handleChangeView(view: string) {
        activeView.value = view;
        if (view !== 'timeGridWeek') {
            currentDate.value = nextVisibleDay(currentDate.value);
        }
        emitDatesChange();
        callbacks.scrollToCurrentTime();
    }

    return {
        activeView,
        currentDate,
        viewDays,
        viewTitle,
        emitDatesChange,
        handlePrev,
        handleNext,
        handleToday,
        handleChangeView,
    };
}
